import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import { consumptionApi, CreateConsumptionDto } from '@/api/services/consumption.api';
import { inventoryApi, InventoryItem, locationsApi, Location } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

const UNITS = ['EA', 'FT', 'M', 'KG', 'LB', 'GAL', 'L', 'BOX', 'SET'];
const OPERATION_PHASES = [
  'DRILLING',
  'COMPLETION',
  'PRODUCTION',
  'TESTING',
  'MAINTENANCE',
  'OTHER',
];
const RATE_UNITS = ['per day', 'per hour', 'per foot', 'per meter', 'per barrel'];

export default function ConsumptionTrackingNew() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [variancePercentage, setVariancePercentage] = useState<number>(0);
  const [requiresApproval, setRequiresApproval] = useState<boolean>(false);

  const [formData, setFormData] = useState({
    itemId: '',
    locationId: '',
    quantity: '',
    unit: 'EA',
    workOrderId: '',
    notes: '',
    plannedQuantity: '',
    varianceReason: '',
    jobId: '',
    jobName: '',
    operationPhase: '',
    consumptionRate: '',
    rateUnit: '',
    baseMetric: '',
    certificateNumbers: '',
  });

  useEffect(() => {
    const loadRefs = async () => {
      try {
        const [itemsRes, locationsRes] = await Promise.all([
          inventoryApi.getAll({ limit: 500 }),
          locationsApi.getAll({ limit: 500 }),
        ]);
        setInventoryItems(itemsRes.data);
        setLocations(locationsRes.data);
      } catch {
        // silently fail
      }
    };
    loadRefs();
  }, []);

  useEffect(() => {
    const planned = parseFloat(formData.plannedQuantity);
    const actual = parseFloat(formData.quantity);
    if (planned && actual && planned > 0) {
      const variance = ((actual - planned) / planned) * 100;
      setVariancePercentage(variance);
      setRequiresApproval(Math.abs(variance) > 10);
    } else {
      setVariancePercentage(0);
      setRequiresApproval(false);
    }
  }, [formData.plannedQuantity, formData.quantity]);

  const handleItemChange = (itemId: string) => {
    const item = inventoryItems.find((i) => i.id === itemId);
    setFormData((prev) => ({
      ...prev,
      itemId,
      unit: item?.unit || 'EA',
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.itemId || !formData.locationId || !formData.quantity) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (requiresApproval && !formData.varianceReason) {
      toast.error('Variance reason is required for variances > 10%');
      return;
    }

    try {
      setSaving(true);
      const dto: CreateConsumptionDto = {
        itemId: formData.itemId,
        locationId: formData.locationId,
        quantity: parseFloat(formData.quantity),
        unit: formData.unit,
        workOrderId: formData.workOrderId || undefined,
        notes: formData.notes || undefined,
        plannedQuantity: formData.plannedQuantity
          ? parseFloat(formData.plannedQuantity)
          : undefined,
        varianceReason: formData.varianceReason || undefined,
        jobId: formData.jobId || undefined,
        jobName: formData.jobName || undefined,
        operationPhase: formData.operationPhase || undefined,
        consumptionRate: formData.consumptionRate
          ? parseFloat(formData.consumptionRate)
          : undefined,
        rateUnit: formData.rateUnit || undefined,
        baseMetric: formData.baseMetric ? parseFloat(formData.baseMetric) : undefined,
        certificateNumbers: formData.certificateNumbers
          ? formData.certificateNumbers
              .split(',')
              .map((c) => c.trim())
              .filter(Boolean)
          : undefined,
      };

      const result = await consumptionApi.record(dto);

      if (result.data.requiresApproval) {
        toast.info('Consumption recorded. Awaiting supervisor approval due to variance.');
      } else {
        toast.success('Consumption recorded successfully');
      }
      navigate('/consumption');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        title="Record Consumption"
        description="Details required to record material issued against a job, well, or location."
        actions={
          <Button variant="outline" onClick={() => navigate('/consumption')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Consumption
          </Button>
        }
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Core Details */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Consumption Details</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="itemId">Inventory Item *</Label>
              <Select value={formData.itemId} onValueChange={handleItemChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an item..." />
                </SelectTrigger>
                <SelectContent>
                  {inventoryItems.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} ({item.sku})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="locationId">Location *</Label>
              <Select
                value={formData.locationId}
                onValueChange={(v) => setFormData({ ...formData, locationId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a location..." />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quantity">Actual Quantity *</Label>
                <Input
                  id="quantity"
                  type="number"
                  step="0.01"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit">Unit</Label>
                <Select
                  value={formData.unit}
                  onValueChange={(v) => setFormData({ ...formData, unit: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map((u) => (
                      <SelectItem key={u} value={u}>
                        {u}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="workOrderId">Work Order ID</Label>
              <Input
                id="workOrderId"
                value={formData.workOrderId}
                onChange={(e) => setFormData({ ...formData, workOrderId: e.target.value })}
                placeholder="Optional work order reference"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
          </div>

          {/* Variance Tracking */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Variance Tracking</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="plannedQuantity">Planned Quantity</Label>
              <Input
                id="plannedQuantity"
                type="number"
                step="0.01"
                value={formData.plannedQuantity}
                onChange={(e) => setFormData({ ...formData, plannedQuantity: e.target.value })}
                placeholder="0"
              />
            </div>

            {formData.plannedQuantity && formData.quantity && (
              <div
                className={`rounded-md p-3 text-sm font-medium ${
                  Math.abs(variancePercentage) > 10
                    ? 'bg-red-50 text-red-700 border border-red-200'
                    : 'bg-green-50 text-green-700 border border-green-200'
                }`}
              >
                Variance: {variancePercentage > 0 ? '+' : ''}
                {variancePercentage.toFixed(1)}%{requiresApproval && ' — Approval required'}
              </div>
            )}

            {requiresApproval && (
              <div className="space-y-2">
                <Label htmlFor="varianceReason">Variance Reason *</Label>
                <Textarea
                  id="varianceReason"
                  value={formData.varianceReason}
                  onChange={(e) => setFormData({ ...formData, varianceReason: e.target.value })}
                  placeholder="Explain the reason for the variance..."
                  rows={3}
                />
              </div>
            )}
          </div>

          {/* Job Information */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Job Information</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="jobId">Job ID</Label>
                <Input
                  id="jobId"
                  value={formData.jobId}
                  onChange={(e) => setFormData({ ...formData, jobId: e.target.value })}
                  placeholder="e.g., JOB-001"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jobName">Job Name</Label>
                <Input
                  id="jobName"
                  value={formData.jobName}
                  onChange={(e) => setFormData({ ...formData, jobName: e.target.value })}
                  placeholder="e.g., Well A Drilling Phase"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="operationPhase">Operation Phase</Label>
              <Select
                value={formData.operationPhase}
                onValueChange={(v) => setFormData({ ...formData, operationPhase: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select phase..." />
                </SelectTrigger>
                <SelectContent>
                  {OPERATION_PHASES.map((phase) => (
                    <SelectItem key={phase} value={phase}>
                      {phase}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Rate & Metrics */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Rate &amp; Metrics</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="consumptionRate">Consumption Rate</Label>
                <Input
                  id="consumptionRate"
                  type="number"
                  step="0.01"
                  value={formData.consumptionRate}
                  onChange={(e) => setFormData({ ...formData, consumptionRate: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rateUnit">Rate Unit</Label>
                <Select
                  value={formData.rateUnit}
                  onValueChange={(v) => setFormData({ ...formData, rateUnit: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select unit..." />
                  </SelectTrigger>
                  <SelectContent>
                    {RATE_UNITS.map((u) => (
                      <SelectItem key={u} value={u}>
                        {u}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="baseMetric">Base Metric</Label>
              <Input
                id="baseMetric"
                type="number"
                step="0.01"
                value={formData.baseMetric}
                onChange={(e) => setFormData({ ...formData, baseMetric: e.target.value })}
                placeholder="e.g., depth in feet"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="certificateNumbers">Certificate Numbers</Label>
              <Input
                id="certificateNumbers"
                value={formData.certificateNumbers}
                onChange={(e) => setFormData({ ...formData, certificateNumbers: e.target.value })}
                placeholder="Comma-separated certificate numbers"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/consumption')}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Recording...' : 'Record Consumption'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
